import React, { useState } from 'react';
import {
  Box,
  HStack,
  VStack,
  Input,
  InputField,
  Switch,
  Text,
  Pressable,
  Icon,
} from '@gluestack-ui/themed';
import { Trash2, ChevronDown, ChevronUp } from 'lucide-react-native';
import { ImageUploader } from './ImageUploader';

export interface MCQOption {
  id: string;
  text: string;
  isCorrect: boolean;
  feedback?: string | null;
  imageUrl?: string | null;
  imageAlt?: string | null;
}

interface OptionEditorProps {
  moduleId: string;
  version: string;
  taskId: string;
  questionId: string;
  option: MCQOption;
  index: number;
  onUpdate: (option: MCQOption) => void;
  onDelete: () => void;
  onToggleCorrect: () => void;
  isReadOnly?: boolean;
  error?: string;
}

export const OptionEditor: React.FC<OptionEditorProps> = ({
  moduleId,
  version,
  taskId,
  questionId,
  option,
  index,
  onUpdate,
  onDelete,
  onToggleCorrect,
  isReadOnly = false,
  error,
}) => {
  const [expanded, setExpanded] = useState(false);
  const letter = String.fromCharCode(65 + index); // A, B, C, D...

  return (
    <Box
      testID={`option-editor-${option.id}`}
      borderWidth={1}
      borderColor={option.isCorrect ? '$brandBase' : '$borderLight'}
      borderRadius="$sm"
      bg={option.isCorrect ? '$bgBrandSubtle' : '$white'}
      overflow="hidden"
    >
      {/* Main Row */}
      <HStack px="$3" py="$2" alignItems="center" space="sm">
        {/* Letter badge */}
        <Box
          w={24}
          h={24}
          borderRadius="$full"
          bg={option.isCorrect ? '$brandBase' : '$bgSubtle'}
          alignItems="center"
          justifyContent="center"
        >
          <Text
            size="xs"
            fontWeight="bold"
            color={option.isCorrect ? '$textOnBrand' : '$textSubtle'}
          >
            {letter}
          </Text>
        </Box>

        {/* Text input */}
        <Input
          testID={`option-editor-${option.id}-text-input`}
          isDisabled={isReadOnly}
          isInvalid={!!error}
          size="sm"
          flex={1}
          variant="underlined"
          borderBottomWidth={0}
        >
          <InputField
            value={option.text}
            onChangeText={(text) => onUpdate({ ...option, text })}
            placeholder={`Option ${letter}`}
          />
        </Input>

        {/* Correct toggle */}
        <HStack space="xs" alignItems="center" flexShrink={0}>
          <Switch
            testID={`option-editor-${option.id}-correct-toggle`}
            value={option.isCorrect}
            onValueChange={onToggleCorrect}
            isDisabled={isReadOnly}
            size="sm"
          />
        </HStack>

        {/* Remove */}
        {!isReadOnly && (
          <Pressable
            testID={`option-editor-${option.id}-remove-button`}
            onPress={onDelete}
            p="$1"
            hitSlop={8}
            opacity={0.4}
            sx={{ _web: { ':hover': { opacity: 1 } } }}
          >
            <Icon as={Trash2} size="xs" color="$textSubtle" />
          </Pressable>
        )}

        {/* Expand/collapse details */}
        {!isReadOnly && (
          <Pressable onPress={() => setExpanded(!expanded)} p="$1" hitSlop={8}>
            <Icon as={expanded ? ChevronUp : ChevronDown} size="xs" color="$textSubtle" />
          </Pressable>
        )}
      </HStack>

      {/* Error */}
      {!!error && (
        <Box px="$3" pb="$1">
          <Text testID={`option-editor-${option.id}-text-error`} size="xs" color="$statusError">
            {error}
          </Text>
        </Box>
      )}

      {/* Expanded details */}
      {expanded && !isReadOnly && (
        <VStack
          space="sm"
          px="$3"
          py="$2"
          borderTopWidth={1}
          borderColor="$borderLight"
          bg="$bgSubtle"
        >
          {/* Feedback */}
          <VStack space="xs">
            <Text size="xs" color="$textSubtle">Feedback (shown after answer)</Text>
            <Input isDisabled={isReadOnly} size="sm">
              <InputField
                testID={`option-editor-${option.id}-feedback-input`}
                value={option.feedback || ''}
                onChangeText={(feedback) => onUpdate({ ...option, feedback })}
                placeholder="Explain why this is right or wrong"
              />
            </Input>
          </VStack>

          {/* Image - state flows from cache via query invalidation */}
          <ImageUploader
            moduleId={moduleId}
            version={version}
            taskId={taskId}
            questionId={questionId}
            optionId={option.id}
            type="option"
            currentImageUrl={option.imageUrl || undefined}
          />

        </VStack>
      )}
    </Box>
  );
};
